import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Spin } from 'antd';
import { CalendarOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type NewsDetailData } from '@/apis/storefrontApi';
import { formatTime } from '@/utils/commonUtils';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

const NewsDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<NewsDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    storefrontApi
      .getNewsDetail(id)
      .then(setItem)
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrap}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <Paragraph>{t('news.notFound')}</Paragraph>
          <Link to="/news">{t('news.backToNews')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <article className={styles.inner}>
        <Link to="/news" className={styles.back}>
          <ArrowLeftOutlined /> {t('news.backToNews')}
        </Link>
        <span className={styles.meta}>
          <CalendarOutlined /> {formatTime(item.publishedAt)}
        </span>
        <Title level={2} className={styles.title}>
          {item.title}
        </Title>
        {item.summary && (
          <Paragraph className={styles.summary}>{item.summary}</Paragraph>
        )}
        {item.coverImage && (
          <div className={styles.cover}>
            <img src={item.coverImage} alt={item.title} />
          </div>
        )}
        {item.body && (
          <div
            className={styles.body}
            dangerouslySetInnerHTML={{ __html: item.body }}
          />
        )}
      </article>
    </div>
  );
};

export default NewsDetail;
